// TODO(real-analysis): swap for real backend endpoint when analysis API is built.
//
// Numbers below match the Anthony Close mockup verbatim, including spots where
// the mockup itself does not internally reconcile (e.g. donut $-amounts sum to
// $6,742 not $5,730; donut percentages sum to 118%). The real analysis API
// will produce reconciled values.

import { monthlyFactor, type MonthlyCadence } from "@fomofi/shared";

export type Confidence = "high" | "medium" | "low";
/**
 * Cadences a recurring INCOME STREAM can have.
 *
 * Exactly the choices on `RecurringTransaction.frequency`
 * (`backend/apps/forecasting/services/../models.py:19-28`) — nothing else can be
 * stored, so nothing else may be offered.
 *
 * `semimonthly` was removed here (BEN-141 3b). It has no backend cadence, so
 * `FinancialAnalysisView` silently mapped it to `biweekly` on save: a user picked
 * "Twice a month", saved, and the Select came back reading "Every 2 weeks" — their
 * stated cadence replaced by a different one, with no error and no notice, on the
 * field that anchors every pay period in the app.
 *
 * NOT to be confused with `UserProfile.pay_frequency` (`@/api/profile`'s
 * `PayFrequency`), which legitimately DOES allow `semimonthly` and `irregular`. That
 * field is a user preference and the no-income-stream fallback for the budgets split;
 * it does not anchor pay periods and is unchanged.
 */
export type IncomeFrequency = MonthlyCadence | "semimonthly";

export type DebtUsage = "pay_in_full" | "carry_balance" | "is_loan" | "ignore";

export const DEBT_USAGE_LABELS: Record<DebtUsage, string> = {
  pay_in_full: "I usually pay in full",
  carry_balance: "I carry a balance",
  is_loan: "This is a loan",
  ignore: "Ignore from debt",
};

export interface IncomeSource {
  id: string;
  name: string;
  type: "Paycheck" | "Deposit" | "Side income" | "Other";
  amountPerPeriod: number;
  frequency: IncomeFrequency;
  /** The two days of a "semimonthly" source (31 = last day of the month), #1385. */
  semimonthlyDays?: number[];
  frequencyLabel: string;
  lastReceived: string;
  confidence: Confidence;
  sparkline: number[];
  /** #715 cross-check against this source's own backing transactions. null when
   *  the backend predates the fields. See ApiIncomeSource. */
  expectedMonthly?: number | null;
  observedMonthly?: number | null;
  matchStatus?: "ok" | "diverges" | "unmatched" | "unverifiable" | null;
}

/** Ticket #715. §1's headline is stream-derived; this is the same figure measured
 *  from the user's income transactions over the same basis months. When the two
 *  disagree materially the card says so instead of asserting a number nothing
 *  corroborates. */
export interface IncomeCrossCheck {
  detectedMonthly: number;
  observedMonthly: number;
  basisMonths: number;
  divergencePct: number | null;
  status: "ok" | "under_detected" | "over_detected" | "no_streams" | "unverifiable";
  caveat: string | null;
}

// `FREQUENCY_LABELS` used to sit here. It is now `CADENCE_LABELS` in
// `@fomofi/shared`, because this map is read only as a FALLBACK for a missing
// server `frequency_label`, and it disagreed with the server it was standing in
// for on two of five cadences ("Every month" for "Monthly", "Every year" for
// "Yearly"). See #755 and `shared/utils/cadence.ts`.

/**
 * One income source's monthly-equivalent amount.
 *
 * The factor comes from the built `@fomofi/shared` runtime package, so web and
 * native cannot drift from one another. The backend contract test still guards
 * the remaining Python-to-TypeScript boundary.
 */
export function monthlyEquivalent(amountPerPeriod: number, frequency: IncomeFrequency): number {
  return amountPerPeriod * monthlyFactor(frequency);
}

/** Total a set of income rows locally.
 *
 *  For UNSAVED rows only: the edit modal's live preview, where the server has
 *  not seen the change yet. The saved figure is `FinancialAnalysis.monthlyIncomeTotal`
 *  (#715) and every display site reads that. */
export function computeMonthlyIncome(sources: IncomeSource[]): number {
  return sources.reduce((sum, s) => sum + monthlyEquivalent(s.amountPerPeriod, s.frequency), 0);
}

export interface Bill {
  id: string;
  merchant: string;
  amount: number;
  /** Detected billing cadence, e.g. "Monthly". Used in the recurring-payments detail modal. */
  frequency?: string;
  /** Last observed transaction date, e.g. "Apr 18, 2024". Used in the detail modal. */
  lastSeen?: string;
}

export interface BillGroup {
  /** Bills are classified as either a "need" or a "want" (client feedback, May 18 meeting). */
  id: "needs" | "wants";
  label: string;
  monthlyTotal: number;
  bills: Bill[];
  showLogoCircles: boolean;
  defaultOpen: boolean;
  visibleCount?: number;
}

export interface CreditCardRow {
  id: string;
  account: string;
  lastFour: string;
  balance: number;
  detection: string;
  detectionConfidence: Confidence;
  usage: DebtUsage;
  /** True only when `usage` is the USER's stored choice, false when it is the
   *  backend heuristic. Drives whether FA §8 may claim a confirmation (4b item 4). */
  usageConfirmed?: boolean;
  /** Estimated scheduled monthly payment (min payment / loan installment). Detail modal only. */
  monthlyPayment?: number;
}

export interface SpendingSegment {
  /** FP-176 extension: `uncategorized` = spending that still needs a category. */
  key: "needs" | "wants" | "savings" | "debt_payments" | "uncategorized";
  label: string;
  amount: number;
  percent: number;
  color: string;
  /** Number of transactions in this category over the analyzed window. Detail modal only. */
  transactionCount?: number;
  /** Top merchants driving this category — at most three, and only merchants with
   *  a non-empty name. Each carries a real transaction id so §4 can correct the
   *  merchant in place (BEN-141 item 1a). Detail modal only. */
  topMerchants?: TopMerchant[];
  /** Contract batch 13 (SC23): every category in this segment, monthly on the
   *  section's divisor, largest first. Detail modal only. */
  categories?: SpendingCategoryAverage[];
}

/** One category's monthly average inside a segment (SC23). */
export interface SpendingCategoryAverage {
  categoryId: number | null;
  name: string;
  amount: number;
  transactionCount: number;
  /** Basis months with at least one row in this category. */
  monthsActive: number;
}

/** A merchant §4 lists, and — when we know a row for it — can re-bucket. */
export interface TopMerchant {
  name: string;
  /** MONTHLY average for this merchant, same divisor as the section's figures. */
  amount: number;
  /** A real transaction id for this merchant; the categorisation POST target. */
  sampleTransactionId: number;
}

export interface MonthlySpending {
  monthlyAverage: number;
  segments: SpendingSegment[];
  /** COMPLETE months behind monthlyAverage. 0 = the divisor fell back to a single
   *  partial month. Below MONTHS_USED_FLOOR the section shows "Insufficient data"
   *  instead of the figure — see @/lib/dataBasis (BEN-141 1c). */
  monthsUsed: number;
  /** Categorisation coverage (BEN-141). `monthlyAverage` and every `percent`
   *  above describe only the spend the backend could categorise; §6 counts the
   *  same rows regardless, which is why the two sections differ. Optional so the
   *  mock fixtures — which have no uncategorised rows — need not restate them.
   *
   *  Expense rows §4 could not place, in the same basis months as the average. */
  uncategorizedCount?: number;
  /** Those rows as a MONTHLY figure — what monthlyAverage falls short by. */
  uncategorizedAmount?: number;
  /** Contract batch 24 (DE20): payments to the user's own linked cards, monthly. */
  cardPaymentsRouted?: number;
  /** CAT-017: outflows filed as Transfer or Income, left out on purpose. */
  excludedCount?: number;
  /** Those rows as a MONTHLY figure. */
  excludedAmount?: number;
  /** Share of basis-month outflow DOLLARS that landed in a segment. 100 (or
   *  undefined) means there is nothing to flag and the caveat is suppressed. */
  coveragePct?: number;
}

export interface TrendPoint {
  month: string;
  amount: number;
  /** Largest discretionary category that month. Detail modal only. */
  topCategory?: string;
  /** A whole month since the user's first transaction. Absent on mock fixtures,
   *  which is read as "complete" (BEN-141 4b item 5). */
  complete?: boolean;
  /** Whether this month fed the averages. */
  inBasis?: boolean;
}

export interface CashFlowPoint {
  month: string;
  income: number;
  expenses: number;
  net: number;
  /** A whole month since the user's first transaction. */
  complete: boolean;
  /** Whether this month fed the averages (differs from `complete` only when the
   *  basis fell back because every complete month was empty). */
  inBasis: boolean;
}

export interface DiscretionaryTrend {
  monthlyAverage: number;
  /** `null` when the basis is too thin to call anything a trend — the backend
   *  floors it at 3 months per half. MUST stay nullable: coalescing to 0 renders
   *  as "flat", which is a claim the data cannot support (BEN-141 1e). */
  deltaPct: number | null;
  /** COMPLETE months behind monthlyAverage. See MonthlySpending.monthsUsed. */
  monthsUsed: number;
  /** Months in EACH half of the deltaPct comparison, so the copy can state the
   *  window it actually measured rather than a hardcoded "6-month". */
  comparisonMonths: number;
  series: TrendPoint[];
  /** Contract batch 14 (SC26, INS-008): rows waiting for review that the figure
   *  leaves out. While count > 0 the figure is provisional. Optional so mock
   *  fixtures need not restate it; absent reads as "nothing waiting". */
  pendingReview?: { isProvisional: boolean; count: number };
}

export interface CashFlowStabilitySnapshot {
  averageNet: number;
  /**
   * Volatility score (0-100). Higher = steadier income/expense pattern.
   * `null` when there are fewer than 3 months of activity (rating = "Insufficient data").
   */
  volatilityScore: number | null;
  rating: "Excellent" | "Good" | "Fair" | "Needs work" | "Insufficient data";
  /** COMPLETE months behind averageNet. The shared caveat signal — insights'
   *  monthsAnalyzed mirrors this exact number, so "Last N months" and the FA
   *  sections cannot claim different windows (BEN-141). */
  monthsUsed: number;
  series: CashFlowPoint[];
}

export interface Insight {
  id: string;
  text: string;
}

export interface FinancialInsights {
  strengths: Insight[];
  risks: Insight[];
  opportunities: Insight[];
}

export interface FinancialAnalysis {
  analyzedRangeLabel: string;
  /** Calendar months the analysis SCANNED — the span `analyzedRangeLabel` names.
   *  Distinct from `spending.monthsUsed`, the count of COMPLETE months every
   *  monthly average divides by, which is normally one lower because the current
   *  month is still running. The header states this one and says "scanned"; §4
   *  states monthsUsed and says "averaged over". One page, two counts, each
   *  labelled for what it counts (BEN-141 B2). null when the backend predates
   *  the field. */
  analyzedWindowMonths: number | null;
  analyzedCompletedDate: string;
  incomeSources: IncomeSource[];
  /** §1's headline, totalled by the server (#715). The same figure as the
   *  backend's `expected_monthly_income`, which is the 50/30/20 denominator, so
   *  the plan and the number it is built from cannot diverge. Read this rather
   *  than reducing `incomeSources`: the rule had four implementations, and the
   *  three outside the server existed only because nothing shipped the answer.
   *  null when the backend predates the field. */
  monthlyIncomeTotal: number | null;
  /** RD-11: every income deposit per month ("Average deposits, last 12 months"). */
  depositsMonthlyAverage?: number | null;
  /** Income (b): paychecks left out of the total, and kept ones. */
  incomeStreamsExcluded?: import("@/api/insights").IncomeStreamFlag[];
  incomeStreamsKept?: import("@/api/insights").IncomeStreamFlag[];
  /** §1 sanity check against transaction-derived income (#715). null when the
   *  backend predates the field. */
  incomeCrossCheck?: IncomeCrossCheck | null;
  totalRecurringPaymentsCount: number;
  billGroups: BillGroup[];
  creditCards: CreditCardRow[];
  spending: MonthlySpending;
  discretionary: DiscretionaryTrend;
  cashFlow: CashFlowStabilitySnapshot;
  insights: FinancialInsights;
}

const MONTHS_12 = [
  "May '23",
  "Jun '23",
  "Jul '23",
  "Aug '23",
  "Sep '23",
  "Oct '23",
  "Nov '23",
  "Dec '23",
  "Jan '24",
  "Feb '24",
  "Mar '24",
  "Apr '24",
];

export const MOCK_FINANCIAL_ANALYSIS: FinancialAnalysis = {
  analyzedRangeLabel: "May 2023 - Apr 2024",
  // Mock stand-in for the server total. It matches the sum of the rows below,
  // which real payloads also do — the point of #715 is that only one of the two
  // is the answer, not that they differ.
  monthlyIncomeTotal: 5000,
  analyzedWindowMonths: 12,
  analyzedCompletedDate: "Apr 22, 2024",

  incomeSources: [
    {
      id: "income-abc",
      name: "ABC Company",
      type: "Paycheck",
      amountPerPeriod: 2184.22,
      frequency: "biweekly",
      frequencyLabel: "Every 2 weeks",
      lastReceived: "Apr 18, 2024",
      confidence: "high",
      sparkline: [2050, 2120, 2080, 2180, 2150, 2160, 2200, 2140, 2180, 2190, 2180, 2184],
    },
    {
      id: "income-side",
      name: "Side Hustle Income",
      type: "Deposit",
      amountPerPeriod: 450,
      frequency: "monthly",
      frequencyLabel: "Every month",
      lastReceived: "Apr 15, 2024",
      confidence: "medium",
      sparkline: [380, 420, 360, 450, 430, 410, 440, 460, 420, 445, 430, 450],
    },
  ],

  totalRecurringPaymentsCount: 24,

  // Per May 18 client feedback, recurring bills are no longer split by fixed /
  // utility / subscription. They are flattened into two groups: Needs and Wants.
  // "Needs" = former Fixed Bills + Utilities (rendered as a flat list).
  // "Wants" = former Subscriptions / Memberships (rendered as logo circles).
  billGroups: [
    {
      id: "needs",
      label: "Needs",
      monthlyTotal: 3277,
      defaultOpen: true,
      showLogoCircles: false,
      bills: [
        {
          id: "rent",
          merchant: "Rent",
          amount: 2100,
          frequency: "Monthly",
          lastSeen: "Apr 1, 2024",
        },
        {
          id: "car-loan",
          merchant: "Car Loan",
          amount: 475,
          frequency: "Monthly",
          lastSeen: "Apr 5, 2024",
        },
        {
          id: "renters-insurance",
          merchant: "Renter's Insurance",
          amount: 68,
          frequency: "Monthly",
          lastSeen: "Apr 3, 2024",
        },
        {
          id: "internet",
          merchant: "Internet",
          amount: 72,
          frequency: "Monthly",
          lastSeen: "Apr 8, 2024",
        },
        {
          id: "phone",
          merchant: "Phone",
          amount: 70,
          frequency: "Monthly",
          lastSeen: "Apr 12, 2024",
        },
        {
          id: "electricity",
          merchant: "Electricity",
          amount: 172,
          frequency: "Monthly",
          lastSeen: "Apr 15, 2024",
        },
        {
          id: "water",
          merchant: "Water",
          amount: 135,
          frequency: "Monthly",
          lastSeen: "Apr 10, 2024",
        },
        { id: "gas", merchant: "Gas", amount: 98, frequency: "Monthly", lastSeen: "Apr 14, 2024" },
        {
          id: "trash",
          merchant: "Trash",
          amount: 87,
          frequency: "Monthly",
          lastSeen: "Apr 6, 2024",
        },
      ],
    },
    {
      id: "wants",
      label: "Wants",
      monthlyTotal: 187,
      defaultOpen: true,
      showLogoCircles: true,
      visibleCount: 4,
      bills: [
        {
          id: "netflix",
          merchant: "Netflix",
          amount: 15.49,
          frequency: "Monthly",
          lastSeen: "Apr 18, 2024",
        },
        {
          id: "spotify",
          merchant: "Spotify",
          amount: 10.99,
          frequency: "Monthly",
          lastSeen: "Apr 20, 2024",
        },
        { id: "gym", merchant: "Gym", amount: 39.0, frequency: "Monthly", lastSeen: "Apr 2, 2024" },
        {
          id: "amazon-prime",
          merchant: "Amazon Prime",
          amount: 14.99,
          frequency: "Monthly",
          lastSeen: "Apr 9, 2024",
        },
        {
          id: "adobe-cc",
          merchant: "Adobe Creative Cloud",
          amount: 54.99,
          frequency: "Monthly",
          lastSeen: "Apr 11, 2024",
        },
        {
          id: "icloud",
          merchant: "iCloud Storage",
          amount: 9.99,
          frequency: "Monthly",
          lastSeen: "Apr 7, 2024",
        },
        {
          id: "chatgpt",
          merchant: "ChatGPT Plus",
          amount: 20.0,
          frequency: "Monthly",
          lastSeen: "Apr 16, 2024",
        },
      ],
    },
  ],

  creditCards: [
    {
      id: "cc-chase",
      account: "Chase Sapphire",
      lastFour: "1234",
      balance: 3842,
      detection: "Usually paid in full",
      detectionConfidence: "high",
      usage: "pay_in_full",
      monthlyPayment: 0,
    },
    {
      id: "cc-discover",
      account: "Discover Card",
      lastFour: "5678",
      balance: 0,
      detection: "Paid in full",
      detectionConfidence: "high",
      usage: "pay_in_full",
      monthlyPayment: 0,
    },
    {
      id: "cc-amex",
      account: "Amex Gold Card",
      lastFour: "9876",
      balance: 1256,
      detection: "Possible revolving debt",
      detectionConfidence: "medium",
      usage: "carry_balance",
      monthlyPayment: 50,
    },
    {
      id: "loan-car",
      account: "Car Loan",
      lastFour: "3456",
      balance: 12450,
      detection: "Installment loan",
      detectionConfidence: "high",
      usage: "is_loan",
      monthlyPayment: 475,
    },
  ],

  spending: {
    monthlyAverage: 5730,
    // A deliberately complete 12-month fixture: this mock exists to exercise the
    // populated layout. The thin-basis states are covered by the unit tests, not
    // here — see MONTHS_USED_FLOOR in @/lib/dataBasis.
    monthsUsed: 12,
    segments: [
      {
        key: "needs",
        label: "Needs",
        amount: 2865,
        percent: 50,
        color: "var(--needs)",
        transactionCount: 86,
        topMerchants: [
          { name: "Rent", amount: 1800, sampleTransactionId: 9001 },
          { name: "Whole Foods", amount: 640, sampleTransactionId: 9002 },
          { name: "Shell", amount: 425, sampleTransactionId: 9003 },
        ],
      },
      {
        key: "wants",
        label: "Wants (Discretionary)",
        amount: 1719,
        percent: 30,
        color: "var(--wants)",
        transactionCount: 124,
        topMerchants: [
          { name: "Amazon", amount: 780, sampleTransactionId: 9101 },
          { name: "DoorDash", amount: 520, sampleTransactionId: 9102 },
          { name: "Starbucks", amount: 419, sampleTransactionId: 9103 },
        ],
      },
      {
        key: "savings",
        label: "Savings",
        amount: 1146,
        percent: 20,
        color: "var(--savings)",
        transactionCount: 14,
        topMerchants: [
          { name: "Vanguard", amount: 600, sampleTransactionId: 9201 },
          { name: "Ally Savings", amount: 350, sampleTransactionId: 9202 },
          { name: "Acorns", amount: 196, sampleTransactionId: 9203 },
        ],
      },
      {
        key: "debt_payments",
        label: "Debt Payments",
        amount: 1012,
        percent: 18,
        color: "var(--debt-allocation)",
        transactionCount: 9,
        topMerchants: [
          { name: "Chase Sapphire", amount: 480, sampleTransactionId: 9301 },
          { name: "Car Loan", amount: 372, sampleTransactionId: 9302 },
          { name: "Amex Gold", amount: 160, sampleTransactionId: 9303 },
        ],
      },
    ],
  },

  discretionary: {
    monthlyAverage: 1719,
    deltaPct: 12,
    monthsUsed: 12,
    // 12 complete months split into equal halves of 6 — comfortably above the
    // 3-per-half floor, so deltaPct is a real trend here.
    comparisonMonths: 6,
    series: [
      { month: MONTHS_12[0], amount: 1480, topCategory: "Dining" },
      { month: MONTHS_12[1], amount: 1530, topCategory: "Travel" },
      { month: MONTHS_12[2], amount: 1310, topCategory: "Shopping" },
      { month: MONTHS_12[3], amount: 1280, topCategory: "Dining" },
      { month: MONTHS_12[4], amount: 1420, topCategory: "Entertainment" },
      { month: MONTHS_12[5], amount: 1610, topCategory: "Shopping" },
      { month: MONTHS_12[6], amount: 1700, topCategory: "Travel" },
      { month: MONTHS_12[7], amount: 1820, topCategory: "Shopping" },
      { month: MONTHS_12[8], amount: 1680, topCategory: "Dining" },
      { month: MONTHS_12[9], amount: 1740, topCategory: "Entertainment" },
      { month: MONTHS_12[10], amount: 1690, topCategory: "Dining" },
      { month: MONTHS_12[11], amount: 1719, topCategory: "Shopping" },
    ],
  },

  cashFlow: {
    averageNet: 1350,
    volatilityScore: 72,
    rating: "Good",
    monthsUsed: 12,
    series: [
      {
        month: MONTHS_12[0],
        income: 6200,
        expenses: 5400,
        net: 800,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[1],
        income: 6300,
        expenses: 5800,
        net: 500,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[2],
        income: 6400,
        expenses: 6900,
        net: -500,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[3],
        income: 6100,
        expenses: 5700,
        net: 400,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[4],
        income: 6400,
        expenses: 5900,
        net: 500,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[5],
        income: 6500,
        expenses: 5200,
        net: 1300,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[6],
        income: 6300,
        expenses: 7400,
        net: -1100,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[7],
        income: 6600,
        expenses: 5500,
        net: 1100,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[8],
        income: 6500,
        expenses: 5300,
        net: 1200,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[9],
        income: 6400,
        expenses: 5100,
        net: 1300,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[10],
        income: 6700,
        expenses: 5400,
        net: 1300,
        complete: true,
        inBasis: true,
      },
      {
        month: MONTHS_12[11],
        income: 6800,
        expenses: 5450,
        net: 1350,
        complete: true,
        inBasis: true,
      },
    ],
  },

  insights: {
    strengths: [
      { id: "s1", text: "You pay your bills on time." },
      { id: "s2", text: "You have positive cash flow most months." },
      { id: "s3", text: "You contribute to savings regularly." },
      { id: "s4", text: "Your recurring obligations stay under 55% of income." },
      { id: "s5", text: "Two of your credit cards are paid in full every month." },
    ],
    risks: [
      {
        id: "r1",
        text: "Your cash balance dips below $100 in the 7 days before payday.",
      },
      {
        id: "r2",
        text: "High spending concentration in the first week of the month.",
      },
      {
        id: "r3",
        text: "Discretionary spending has trended up 12% over the last 6 months.",
      },
      {
        id: "r4",
        text: "Two months in the last year ended with negative cash flow.",
      },
    ],
    opportunities: [
      {
        id: "o1",
        text: "Reducing discretionary spending by 15% could free up ~$260/mo.",
      },
      {
        id: "o2",
        text: "Consider building an emergency fund of 1-3 months of expenses.",
      },
      {
        id: "o3",
        text: "Paying down the Amex Gold balance could save ~$28/mo in interest.",
      },
      {
        id: "o4",
        text: "Automating savings on payday could smooth your pre-payday dips.",
      },
    ],
  },
};
